import { createBrowserHistory } from "history";
import { Suspense, lazy, useCallback, useEffect } from "react";
import { Redirect, Route, Router, Switch } from "react-router-dom";
import { api, setApiUrl, setAppUrl } from "@common/helpers/api";
import { clearApiConnectionIssue } from "@common/helpers/apiConnectionStatus";
import { setNavigation } from "@common/helpers/navigation";
import Providers from "@common/providers";
import { LoadingState } from "./elements/container";
import AppErrorBoundary from "./elements/errors/AppErrorBoundary";
import { LoadProgress } from "./elements/loading";
import { initSentry } from "./helpers/sentry";
import {
  getDirectionFromLocale,
  getInitialAppLocale,
} from "./helpers/publicLocale";
import { recoverFromStaleChunk } from "./helpers/staleChunkRecovery";
import {
  DirectionProvider,
  syncDocumentDirection,
  useLocalStorage,
} from "./hooks";

const ReactQueryDevtools =
  import.meta.env.DEV && import.meta.env.VITE_SHOW_QUERY_DEVTOOLS === "1"
    ? lazy(() =>
        import("@tanstack/react-query-devtools").then((module) => ({
          default: module.ReactQueryDevtools,
        })),
      )
    : undefined;

setApiUrl(import.meta.env.VITE_API_URL);
setAppUrl(import.meta.env.VITE_APP_URL);
const initialLocale = getInitialAppLocale();
syncDocumentDirection({
  direction: getDirectionFromLocale(initialLocale),
  language: initialLocale,
});

window.addEventListener("vite:preloadError", (event) => {
  if (recoverFromStaleChunk(event.payload)) {
    event.preventDefault();
  }
});

const history = createBrowserHistory();

// Defer Sentry init until the browser is idle so it doesn't compete with
// first paint or initial route hydration. Falls back to a short timeout
// on browsers without requestIdleCallback (Safari < 16).
function scheduleSentryInit() {
  const run = () => initSentry(history);
  if (typeof window === "undefined") return;
  const ric = (window as any).requestIdleCallback as
    | ((cb: () => void, opts?: { timeout: number }) => number)
    | undefined;
  if (ric) {
    ric(run, { timeout: 4000 });
  } else {
    window.setTimeout(run, 2000);
  }
}

scheduleSentryInit();

let loadProgressHideTimer: ReturnType<typeof setTimeout> | undefined;

function loadProgress(percent: number) {
  const progressItems = document.querySelectorAll<HTMLElement>(
    "[data-load-progress]",
  );
  if (progressItems.length === 0) {
    return;
  }

  if (loadProgressHideTimer) {
    clearTimeout(loadProgressHideTimer);
    loadProgressHideTimer = undefined;
  }

  progressItems.forEach((progress) => {
    const bar = progress.querySelector<HTMLElement>("[data-load-progress-bar]");

    progress.setAttribute("aria-valuenow", String(percent));
    if (bar) {
      bar.style.width = percent + "%";
    }
  });

  if (percent === 100) {
    loadProgressHideTimer = setTimeout(() => {
      progressItems.forEach((progress) => {
        const bar = progress.querySelector<HTMLElement>(
          "[data-load-progress-bar]",
        );

        progress.classList.add("hidden");
        progress.classList.remove("block");
        progress.setAttribute("aria-hidden", "true");
        progress.setAttribute("aria-valuenow", "0");
        if (bar) {
          bar.style.width = "0%";
        }
      });
      loadProgressHideTimer = undefined;
    }, 450);
  } else {
    progressItems.forEach((progress) => {
      progress.classList.remove("hidden");
      progress.classList.add("block");
      progress.setAttribute("aria-hidden", "false");
    });
  }
}

api.interceptors.request.use(
  function (config: any) {
    if (!config.meta?.hideLoading) {
      loadProgress(10);
      loadProgress(50);
    }
    return config;
  },
  function (error) {
    loadProgress(100);
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  function (response) {
    clearApiConnectionIssue();
    loadProgress(100);
    return response;
  },
  function (error) {
    loadProgress(100);
    return Promise.reject(error);
  },
);

export type LayoutComponentProps = {
  isPublic?: boolean;
  children: React.ReactNode;
};

// #TODO: its important to check all navigation paths, maybe we can create a const
// for each navigation path?
setNavigation({
  push: (path) => history.push("/" + path),
});

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4 py-6">
      <LoadingState size="page" surface="plain" />
    </div>
  );
}

const App = ({
  routes,
  layout: Layout,
}: {
  routes: {
    [key in "publicApps" | "apps" | "errors"]?: any;
  };
  layout: React.ComponentType<LayoutComponentProps>;
}) => {
  const [theme, setTheme] = useLocalStorage<"light" | "dark">(
    "theme",
    window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light",
  );
  const [installedVersion, setInstalledVersion] = useLocalStorage(
    "installed_version",
    "0.0.0",
  );

  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    (window as any).__syncBrandPrimaryPalette?.();
  }, [theme]);

  return (
    <AppErrorBoundary>
      <DirectionProvider>
        <Providers
          theme={theme}
          toggleTheme={useCallback(() => {
            setTheme((theme) => (theme === "light" ? "dark" : "light"));
          }, [setTheme])}
          installedVersion={installedVersion}
          onInstall={setInstalledVersion}
        >
          <LoadProgress />
          <Router history={history}>
            <Switch>
              {routes.publicApps && (
                <Route path={Object.keys(routes.publicApps)}>
                  <Layout isPublic={true}>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <Switch>
                        {Object.keys(routes.publicApps).map((path) => (
                          <Route
                            path={path}
                            key={path}
                            render={routes.publicApps[path]}
                          />
                        ))}
                      </Switch>
                    </Suspense>
                  </Layout>
                </Route>
              )}
              {routes.apps && (
                <Route path={Object.keys(routes.apps)}>
                  <Layout>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <Switch>
                        {Object.keys(routes.apps).map((path) => (
                          <Route
                            path={path}
                            key={path}
                            render={routes.apps[path]}
                            exact={path === "/"}
                          />
                        ))}
                        <Route
                          render={({ location }) => {
                            if (location.pathname === "/") {
                              return (
                                <Redirect
                                  to={{
                                    pathname: "/dashboard",
                                  }}
                                />
                              );
                            }
                            const Component = routes.errors[404];
                            return <Component />;
                          }}
                        />
                      </Switch>
                    </Suspense>
                  </Layout>
                </Route>
              )}
              <Route path="*">
                <Layout>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <Switch>
                      <Route
                        render={() => {
                          const Component = routes.errors[500];
                          return <Component />;
                        }}
                        path="/error"
                      />
                      <Route
                        render={({ location }) => {
                          if (location.pathname === "/") {
                            return (
                              <Redirect
                                to={{
                                  pathname: "/dashboard",
                                }}
                              />
                            );
                          }
                          const Component = routes.errors[404];
                          return <Component />;
                        }}
                      />
                    </Switch>
                  </Suspense>
                </Layout>
              </Route>
            </Switch>
          </Router>
          {ReactQueryDevtools && (
            <Suspense fallback={null}>
              <ReactQueryDevtools initialIsOpen={false} />
            </Suspense>
          )}
        </Providers>
      </DirectionProvider>
    </AppErrorBoundary>
  );
};

export default App;
